import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface ClientInfo {
  id: string; name: string;
  payment_terms_days: number | null;
  payment_terms_anchor: string | null;
  payment_history_score: number | null;
  credit_limit_sar: string | null;
}
interface HistoryRow {
  id: string; flatbed_trip_id: string;
  invoice_amount_sar: string | null; expected_payment_date: string | null;
  actual_payment_date: string | null; days_late: number | null;
  amount_received_sar: string | null; variance_sar: string | null;
  recorded_at: string;
}
interface ReliabilityResponse {
  client: ClientInfo | null;
  history: HistoryRow[];
  stats: { avg_days_late: number | null; on_time_count: number; late_count: number; total_count: number } | null;
}

const ANCHORS = ['trip_date','delivery_date','invoice_date','end_of_month'] as const;

export default function ClientPaymentTermsPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<ReliabilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [termsDays,    setTermsDays]    = useState<string>('30');
  const [anchor,       setAnchor]       = useState<string>('delivery_date');
  const [creditLimit,  setCreditLimit]  = useState<string>('');

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const r = await apiClient.get<ReliabilityResponse>('/api/v1/cash-flow/clients/' + id + '/reliability');
      setData(r.data);
      if (r.data.client) {
        setTermsDays(String(r.data.client.payment_terms_days ?? 30));
        setAnchor(r.data.client.payment_terms_anchor ?? 'delivery_date');
        setCreditLimit(r.data.client.credit_limit_sar ?? '');
      }
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  async function save() {
    if (!id) return;
    setSaving(true);
    try {
      const days = Number(termsDays);
      const limit = creditLimit === '' ? null : Number(creditLimit);
      await apiClient.patch('/api/v1/cash-flow/clients/' + id + '/terms', {
        payment_terms_days:   Number.isFinite(days) ? days : undefined,
        payment_terms_anchor: anchor,
        credit_limit_sar:     limit,
      });
      await refresh();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (error || !data || !data.client) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-sm text-muted-foreground">{error ?? 'Client not found'}</div>
      <Link to="/dashboard/flatbed/cash-flow"><Button variant="outline" size="sm">Back to cash flow</Button></Link>
    </div>
  );

  const c = data.client;
  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/flatbed/cash-flow"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Cash flow</Button></Link>
          <div className="text-sm font-medium">{c.name}</div>
          <div className="w-12" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Score" value={String(c.payment_history_score ?? '—')} tone={c.payment_history_score != null && c.payment_history_score < 70 ? 'warn' : 'good'} />
          <Stat label="Avg days late" value={data.stats?.avg_days_late != null ? data.stats.avg_days_late.toFixed(1) : '—'} />
          <Stat label="On-time / total" value={(data.stats?.on_time_count ?? 0) + ' / ' + (data.stats?.total_count ?? 0)} />
        </div>

        <div className="rounded-lg border border-border p-4 space-y-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Payment terms</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Days</Label>
              <Input type="number" min={0} max={365} value={termsDays} onChange={(e) => setTermsDays(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Anchor</Label>
              <select value={anchor} onChange={(e) => setAnchor(e.target.value)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                {ANCHORS.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Credit limit (SAR, blank = no limit)</Label>
              <Input type="number" min={0} step="0.01" value={creditLimit} onChange={(e) => setCreditLimit(e.target.value)} />
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </Button>
        </div>

        <div className="rounded-lg border border-border overflow-hidden">
          <div className="px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
            Payment history ({data.history.length})
          </div>
          {data.history.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">No payment history yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs text-muted-foreground bg-muted/20 border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2">Recorded</th>
                    <th className="text-left px-3 py-2">Expected</th>
                    <th className="text-left px-3 py-2">Paid</th>
                    <th className="text-right px-3 py-2">Days late</th>
                    <th className="text-right px-3 py-2">Invoice</th>
                    <th className="text-right px-3 py-2">Received</th>
                    <th className="text-right px-3 py-2">Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.history.map((h) => (
                    <tr key={h.id} className="border-b border-border last:border-b-0">
                      <td className="px-3 py-2 text-xs font-mono">{new Date(h.recorded_at).toLocaleDateString('en-GB')}</td>
                      <td className="px-3 py-2 text-xs">{h.expected_payment_date ?? '—'}</td>
                      <td className="px-3 py-2 text-xs">{h.actual_payment_date ?? '—'}</td>
                      <td className={'px-3 py-2 text-right text-xs font-medium ' + (h.days_late != null && h.days_late > 0 ? 'text-rose-700 dark:text-rose-400' : '')}>{h.days_late ?? '—'}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{h.invoice_amount_sar ? fmtSar(Number(h.invoice_amount_sar)) : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{h.amount_received_sar ? fmtSar(Number(h.amount_received_sar)) : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{h.variance_sar ? fmtSar(Number(h.variance_sar)) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'warn' }) {
  const cls = tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : tone === 'warn' ? 'text-rose-600 dark:text-rose-400' : '';
  return (
    <div className="bg-muted rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={'text-xl font-medium mt-0.5 ' + cls}>{value}</div>
    </div>
  );
}
